import { GhostData, SAVE_VERSION, GHOST_VERSION, bufferToBase64, base64ToBuffer } from '@voidline/shared';

// ── settings ────────────────────────────────────────────────────────────────

export type GraphicsPreset = 'performance' | 'balanced' | 'quality';

export interface BindingSet {
  /** KeyboardEvent.code values. */
  keys: string[];
  /** Gamepad button indices. */
  padButtons: number[];
}

export type Action =
  | 'throttle'
  | 'brake'
  | 'steerLeft'
  | 'steerRight'
  | 'drift'
  | 'boost'
  | 'respawn'
  | 'pitchUp'
  | 'pitchDown'
  | 'lookBack'
  | 'pause';

export const DEFAULT_BINDINGS: Record<Action, BindingSet> = {
  throttle: { keys: ['KeyW', 'ArrowUp'], padButtons: [7] },
  brake: { keys: ['KeyS', 'ArrowDown'], padButtons: [6] },
  steerLeft: { keys: ['KeyA', 'ArrowLeft'], padButtons: [14] },
  steerRight: { keys: ['KeyD', 'ArrowRight'], padButtons: [15] },
  // hold + steer: the airbrake is applied on the side you're steering toward
  drift: { keys: ['Space'], padButtons: [4, 5] },
  boost: { keys: ['ShiftLeft'], padButtons: [0] },
  respawn: { keys: ['KeyR'], padButtons: [3] },
  pitchUp: { keys: ['KeyK'], padButtons: [] },
  pitchDown: { keys: ['KeyI'], padButtons: [] },
  lookBack: { keys: ['KeyC'], padButtons: [10] },
  pause: { keys: ['Escape'], padButtons: [9] },
};

export interface Settings {
  version: number;
  playerName: string;
  graphicsPreset: GraphicsPreset;
  renderScale: number;
  baseFov: number;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  ambientVolume: number;
  reducedMotion: boolean;
  effectIntensity: number;
  cameraShake: number;
  steerSensitivity: number;
  invertPitch: boolean;
  speedUnits: 'kmh' | 'vl';
  colorAssist: boolean;
  showGhost: boolean;
  bindings: Record<Action, BindingSet>;
  serverUrl: string;
  assists: {
    steeringHelp: boolean; // gentle magnetic centering
    brakeHelp: boolean; // auto airbrake into hard corners
    resetHelp: boolean; // faster auto-reset when falling
  };
  firstRunDone: boolean;
  tutorialDone: boolean;
}

export function defaultSettings(): Settings {
  return {
    version: SAVE_VERSION,
    playerName: '',
    graphicsPreset: 'balanced',
    renderScale: 1,
    baseFov: 72,
    masterVolume: 0.8,
    musicVolume: 0.65,
    sfxVolume: 0.85,
    ambientVolume: 0.7,
    reducedMotion: false,
    effectIntensity: 1,
    cameraShake: 0.8,
    steerSensitivity: 1,
    invertPitch: false,
    speedUnits: 'kmh',
    colorAssist: false,
    showGhost: true,
    bindings: structuredClone(DEFAULT_BINDINGS),
    serverUrl: '',
    assists: { steeringHelp: false, brakeHelp: false, resetHelp: true },
    firstRunDone: false,
    tutorialDone: false,
  };
}

// ── progression ─────────────────────────────────────────────────────────────

export type MedalTier = 'bronze' | 'silver' | 'gold' | 'author';

export interface TrackRecord {
  bestLapTicks: number;
  bestRaceTicks: number;
  medal: MedalTier | null;
  races: number;
  wins: number;
}

export interface Progression {
  version: number;
  xp: number;
  races: number;
  wins: number;
  podiums: number;
  distanceKm: number;
  boostSeconds: number;
  records: Record<string, TrackRecord>;
  vehicleStats: Record<string, { races: number; wins: number; distanceKm: number }>;
  achievements: Record<string, number>; // id → unix ms
  championships: Record<string, { stage: number; points: number; done: boolean }>;
  dailyBest: Record<string, number>; // seed → ticks
  selectedVehicle: string;
  selectedPaints: Record<string, string>; // vehicleId → paintId
}

export function defaultProgression(): Progression {
  return {
    version: SAVE_VERSION,
    xp: 0,
    races: 0,
    wins: 0,
    podiums: 0,
    distanceKm: 0,
    boostSeconds: 0,
    records: {},
    vehicleStats: {},
    achievements: {},
    championships: {},
    dailyBest: {},
    selectedVehicle: 'kestrel',
    selectedPaints: {},
  };
}

export function levelForXp(xp: number): number {
  return Math.floor(Math.sqrt(Math.max(0, xp) / 90)) + 1;
}
export function xpForLevel(level: number): number {
  return (level - 1) * (level - 1) * 90;
}

// ── storage plumbing (defensive: malformed data can never crash the game) ──

const NS = 'voidline';

function loadJson<T>(key: string, fallback: () => T, validate: (v: any) => boolean): T {
  try {
    const raw = localStorage.getItem(`${NS}.${key}`);
    if (!raw) return fallback();
    const parsed = JSON.parse(raw);
    if (!validate(parsed)) {
      console.warn(`[persist] discarding invalid save "${key}"`);
      return fallback();
    }
    return parsed as T;
  } catch (err) {
    console.warn(`[persist] failed to load "${key}"`, err);
    return fallback();
  }
}

function saveJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(`${NS}.${key}`, JSON.stringify(value));
  } catch (err) {
    console.warn(`[persist] failed to save "${key}"`, err);
  }
}

export function loadSettings(): Settings {
  const s = loadJson<Settings>(
    'settings',
    defaultSettings,
    (v) => typeof v === 'object' && v !== null && typeof v.version === 'number'
  );
  // migrate/merge: fill any missing fields from defaults so old saves survive upgrades
  const merged = { ...defaultSettings(), ...s, version: SAVE_VERSION };
  // bindings: rebuild from the current action list. Saves from before the
  // drift-button scheme (no 'drift' action) are reset wholesale — their old
  // Space=boost / Q/E=airbrake entries would otherwise shadow the new defaults.
  const base = structuredClone(DEFAULT_BINDINGS);
  const savedBindings = (s.bindings ?? {}) as Partial<Record<Action, BindingSet>>;
  if (savedBindings.drift) {
    for (const action of Object.keys(base) as Action[]) {
      if (savedBindings[action]) base[action] = savedBindings[action]!;
    }
  }
  merged.bindings = base;
  merged.assists = { ...defaultSettings().assists, ...(s.assists ?? {}) };
  return merged;
}

export function saveSettings(s: Settings): void {
  saveJson('settings', s);
}

export function loadProgression(): Progression {
  const p = loadJson<Progression>(
    'progression',
    defaultProgression,
    (v) => typeof v === 'object' && v !== null && typeof v.xp === 'number'
  );
  return { ...defaultProgression(), ...p, version: SAVE_VERSION };
}

export function saveProgression(p: Progression): void {
  saveJson('progression', p);
}

// ── ghosts ──────────────────────────────────────────────────────────────────

interface StoredGhost extends Omit<GhostData, 'frames'> {
  framesB64: string;
}

export function ghostKey(trackId: string, seed: string | null): string {
  return seed ? `seed:${seed}` : trackId;
}

export function loadGhost(trackId: string, seed: string | null): GhostData | null {
  try {
    const raw = localStorage.getItem(`${NS}.ghost.${ghostKey(trackId, seed)}`);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredGhost;
    if (stored.version !== GHOST_VERSION) {
      console.warn('[persist] ghost version mismatch — ignoring old ghost');
      return null;
    }
    if (typeof stored.framesB64 !== 'string' || typeof stored.totalTicks !== 'number') return null;
    const { framesB64, ...rest } = stored;
    return { ...rest, frames: base64ToBuffer(framesB64) };
  } catch (err) {
    console.warn('[persist] failed to load ghost', err);
    return null;
  }
}

export function saveGhost(ghost: GhostData): void {
  try {
    const { frames, ...rest } = ghost;
    const stored: StoredGhost = { ...rest, framesB64: bufferToBase64(frames) };
    localStorage.setItem(`${NS}.ghost.${ghostKey(ghost.trackId, ghost.trackSeed)}`, JSON.stringify(stored));
  } catch (err) {
    console.warn('[persist] failed to save ghost (storage full?)', err);
  }
}

export function deleteGhost(trackId: string, seed: string | null): void {
  try {
    localStorage.removeItem(`${NS}.ghost.${ghostKey(trackId, seed)}`);
  } catch {}
}

/** Export a ghost as a shareable JSON string. */
export function exportGhost(ghost: GhostData): string {
  const { frames, ...rest } = ghost;
  return JSON.stringify({ ...rest, framesB64: bufferToBase64(frames) });
}

export function importGhost(json: string): GhostData | null {
  try {
    const stored = JSON.parse(json) as StoredGhost;
    if (stored.version !== GHOST_VERSION || typeof stored.framesB64 !== 'string') return null;
    const { framesB64, ...rest } = stored;
    return { ...rest, frames: base64ToBuffer(framesB64) };
  } catch {
    return null;
  }
}

// ── session resume token (reconnects across refreshes) ─────────────────────

export function loadResumeToken(): string | null {
  try {
    return sessionStorage.getItem(`${NS}.resume`);
  } catch {
    return null;
  }
}
export function saveResumeToken(token: string): void {
  try {
    sessionStorage.setItem(`${NS}.resume`, token);
  } catch {}
}
